// plan-05 S06: decode mode — brute-force vocabulary scan + cosine similarity

import type { HexGrid } from './geometry'
import { strHash, makePRNG } from './prng'

export interface DecodeResult {
  word: string
  score: number
}

function simHits(word: string, total: number, centerSeq: number): number[] {
  const seed = strHash(word)
  const rng = makePRNG(seed)
  const h = new Array(total).fill(0)
  for (let i = 0; i < 500; i++) h[Math.floor(rng() * total)]++
  const mx = Math.max(...h)
  h[centerSeq] = mx
  return h
}

function cosineSim(a: number[], b: number[]): number {
  let dot = 0, na = 0, nb = 0
  for (let i = 0; i < a.length; i++) {
    dot += a[i] * b[i]
    na += a[i] * a[i]
    nb += b[i] * b[i]
  }
  return (na === 0 || nb === 0) ? 0 : dot / (Math.sqrt(na) * Math.sqrt(nb))
}

export function decode(
  target: number[],
  grid: HexGrid,
  vocab: string[],
  onProgress?: (pct: number) => void,
): Promise<DecodeResult> {
  const { total, centerHex } = grid

  return new Promise((resolve) => {
    let bestWord = ''
    let bestScore = -1
    let idx = 0
    const CHUNK = 50

    function processChunk(): void {
      const end = Math.min(idx + CHUNK, vocab.length)
      for (; idx < end; idx++) {
        const score = cosineSim(target, simHits(vocab[idx], total, centerHex.seqNum))
        if (score > bestScore) {
          bestScore = score
          bestWord = vocab[idx]
        }
      }
      if (idx < vocab.length) {
        onProgress?.(Math.round(idx / vocab.length * 100))
        setTimeout(processChunk, 0)
      } else {
        resolve({ word: bestWord, score: bestScore })
      }
    }

    setTimeout(processChunk, 0)
  })
}

export function buildVocab(
  slotX: string[], slotY: string[], slotZ: string[], slotA: string[],
): string[] {
  const vocab: string[] = [...slotX, ...slotY, ...slotZ, ...slotA]
  const sampleSize = Math.min(slotX.length * slotY.length * slotZ.length * slotA.length, 2000)
  const seen = new Set<string>()
  let attempts = 0

  const baseLen = vocab.length
  while (vocab.length - baseLen < sampleSize && attempts < sampleSize * 4) {
    attempts++
    const xi = Math.floor(Math.random() * slotX.length)
    const yi = Math.floor(Math.random() * slotY.length)
    const zi = Math.floor(Math.random() * slotZ.length)
    const ai = Math.floor(Math.random() * slotA.length)
    const key = `${xi},${yi},${zi},${ai}`
    if (seen.has(key)) continue
    seen.add(key)
    vocab.push(`${slotX[xi]} ${slotY[yi]}, but ${slotZ[zi]} ${slotA[ai]}`)
  }

  return vocab
}
